import { useState, useEffect } from 'react';
import { useOnboardingStore, UploadedMethodType, type PreviousProduct } from '../../../../../store/onboardingStore';
import { toBase64 } from '../../../../../utils/utils';
import { uploadImagesToS3 } from '../../../../../services/s3/s3Service';
import toast from 'react-hot-toast';
import { FOLDER_IMAGES } from '../../../../onboarding/const/products';
import { dictionary } from '../../../../../utils/dictionary';
import { useAuth } from '../../../../../context/AuthContext';
import type { UpsellProduct } from '../types';
import { createEmptyUpsell } from '../types';
import { sanitizeUploadFilename } from '../../../../../utils/sanitizeUploadFilename';

export function useUpsells() {
  const { data, updateAdditionalOffers } = useOnboardingStore();
  const { additionalOffers } = data;
  const { language } = useAuth();

  const [upsells, setUpsells] = useState<UpsellProduct[]>(() =>
    additionalOffers.upsell?.enabled && additionalOffers.upsell?.products?.length
      ? additionalOffers.upsell.products
      : [createEmptyUpsell()]
  );

  const [toggleEnabled, setToggleEnabled] = useState(additionalOffers.upsell?.enabled ?? false);
  const [methods, setMethods] = useState<Map<string, 'link' | 'manual' | 'previous' | null>>(new Map());
  const [imageUploadStates, setImageUploadStates] = useState<Map<string, { isLoading: boolean }>>(new Map());
  const [editingId, setEditingId] = useState<string | null>(null);

  useEffect(() => {
    if (!toggleEnabled) {
      setUpsells([]);
      updateAdditionalOffers({ ...additionalOffers, upsell: { enabled: false, products: [] } });
    }
  }, [toggleEnabled]);

  useEffect(() => {
    if (!additionalOffers.upsell) {
      updateAdditionalOffers({
        upsell: { enabled: true, products: upsells },
        bumpOffer: {
          enabled: false,
          productName: '',
          price: '0.00',
          description: '',
          image: undefined,
          url_images: undefined,
          isManualEntry: false,
          productLink: '',
        },
      });
    }
  }, []);

  useEffect(() => {
    if (additionalOffers.upsell?.products?.length) {
      setUpsells(additionalOffers.upsell.products);
      const map = new Map<string, 'link' | 'manual' | 'previous' | null>();
      additionalOffers.upsell.products.forEach((p) => {
        if (p.uploadedMethod === UploadedMethodType.LINK) map.set(p.id, 'link');
        else if (p.uploadedMethod === UploadedMethodType.MANUAL) map.set(p.id, 'manual');
        else if (p.uploadedMethod === UploadedMethodType.PREVIOUS) map.set(p.id, 'previous');
        else map.set(p.id, null);
      });
      setMethods(map);
    }
  }, [additionalOffers]);

  const syncUpsellsToStore = (next: UpsellProduct[]) => {
    setUpsells(next);
    updateAdditionalOffers({
      ...additionalOffers,
      upsell: { enabled: true, products: next },
    });
  };

  const handleToggle = (enabled: boolean) => {
    setToggleEnabled(enabled);
    if (enabled && upsells.length < 1) {
      const newUpsell = createEmptyUpsell();
      const next = [...upsells, newUpsell];
      setUpsells(next);
      setEditingId(newUpsell.id);
      updateAdditionalOffers({ ...additionalOffers, upsell: { enabled: true, products: next } });
    }
  };

  const handleAdd = () => {
    if (upsells.length >= 1) return;
    const newUpsell = createEmptyUpsell();
    const next = [...upsells, newUpsell];
    setUpsells(next);
    setEditingId(newUpsell.id);
    updateAdditionalOffers({ ...additionalOffers, upsell: { enabled: true, products: next } });
  };

  const handleChange = (id: string, field: keyof UpsellProduct, value: unknown) => {
    const next = upsells.map((u) => {
      if (u.id !== id) return u;
      const updated = { ...u, [field]: value };
      if (field === 'uploadedMethod' && value !== UploadedMethodType.PREVIOUS) {
        updated.previousProductSnapshot = null;
      }
      return updated;
    });
    syncUpsellsToStore(next);
  };

  const setMethod = (upsellId: string, newMethod: 'link' | 'manual' | 'previous') => {
    setMethods((m) => new Map(m.set(upsellId, newMethod)));
    const map = {
      link: UploadedMethodType.LINK,
      manual: UploadedMethodType.MANUAL,
      previous: UploadedMethodType.PREVIOUS,
    };
    handleChange(upsellId, 'uploadedMethod', map[newMethod]);
  };

  const handlePreviousSelect = (
    product: PreviousProduct,
    upsellId: string
  ) => {
    if (!product) return;
    const next = upsells.map((u) =>
      u.id === upsellId
        ? {
            ...u,
            productName: product.name,
            price: String(product.price),
            description: product.description || '',
            isManualEntry: false,
            image: '',
            url_images: product.images?.length ? [product.images[0]] : undefined,
            productLink: '',
            externalId: product.id,
            providerId: 'INTERNAL_FLUXI',
            uploadedMethod: UploadedMethodType.PREVIOUS,
            previousProductSnapshot: {
              id: product.id,
              price: String(product.price),
              countryCode: product.country_code,
              currencyCode: product.currency_code,
            },
          }
        : u
    );
    syncUpsellsToStore(next);
    setEditingId(upsellId);
  };

  const handleImageSelect = async (file: File, upsellId?: string) => {
    if (!upsellId) return;
    setImageUploadStates((p) => new Map(p.set(upsellId, { isLoading: true })));
    try {
      const base64File = await toBase64(file);
      const response = await uploadImagesToS3({
        file: base64File,
        folder: FOLDER_IMAGES,
        filename: `${Date.now()}-${sanitizeUploadFilename(file.name)}`,
      });
      const imageUrl = [response.s3_url];
      const next = upsells.map((u) =>
        u.id === upsellId
          ? {
              ...u,
              image: undefined,
              uploadedMethod: UploadedMethodType.MANUAL,
              url_images: imageUrl,
              previousProductSnapshot: null,
            }
          : u
      );
      syncUpsellsToStore(next);
      toast.success(dictionary?.['success-uploading-images']?.[language] ?? 'Imagen subida exitosamente');
    } catch (err) {
      console.error(err);
      toast.error(dictionary?.['error-uploading-images-too-long']?.[language] ?? 'Error al subir la imagen');
    } finally {
      setImageUploadStates((p) => new Map(p.set(upsellId, { isLoading: false })));
    }
  };

  return {
    upsells,
    toggleEnabled,
    methods,
    imageUploadStates,
    editingId,
    setEditingId,
    handleToggle,
    handleAdd,
    handleChange,
    setMethod,
    handlePreviousSelect,
    handleImageSelect,
  };
}
